import React from 'react';

import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import { EditorHeader } from '../EditorHeader';
import FormSwitch from '../FormSwitch';
import TypeAheadSelect from '../TypeAheadSelect';
import { ModalOKCancel } from '../../modals';
import api from '../../ApiService';
import { UserRelationshipTypes, UserRoles } from '../../helper';


export default class UserEditor extends React.Component {
    constructor(props) {
        super(props);

        this.getEmptyEditModel = this.getEmptyEditModel.bind();
        this.handleChange = this.handleChange.bind();
        this.handleHospitalChange = this.handleHospitalChange.bind();
        this.handleSelectChange = this.handleSelectChange.bind();
        this.handleCheckboxChange = this.handleCheckboxChange.bind();

        this.state = {
            isDirty: false,
            isLoading: true,
            editModel: this.getEmptyEditModel(),
            originalEmail: null,
            selectedHospitalDropdown: null,
            selectedAffiliateDropdown: null,
            showResetPasswordModal: false,
            showCancelModal: false,
            hospitals: [],
            affiliates: [],
            affiliateGroups: [],
            gpos: [],
            gpoGroups: [],
            showWelcomeEmailModal: false,
            welcomeEmailSent: ''
        };
    }

    componentDidMount = async() => {
        // Grab data from server.
        let editModel = await api.getUserEditModel(this.props.userId);
        let originalEmail = editModel.email;
        let hospitals = await api.getHospitals();
        let affiliates = await api.getLookup('Affiliates');
        let affiliateGroups = await api.getLookup('AffiliateGroups');
        let gpos = await api.getLookup('GpoOptions');
        let gpoGroups = await api.getLookup('GpoGroups');

        // Set hospital dropdown, if selected.
        let selectedHospitalDropdown = null;
        if (editModel.userRelationshipType === 'Hospital') {

            let hospital = hospitals.find(function (h) {
                return h.id === editModel.userRelationshipId;
            });

            if (hospital !== null) {
                selectedHospitalDropdown = {
                    value: hospital.name,
                    label: hospital.name + ' - ' + hospital.description,
                    id: hospital.id
                };
            }
        }

        // Set affiliate dropdown, if selected.
        let selectedAffiliateDropdown = null;
        if (editModel.userRelationshipType === 'Affiliate' || editModel.userRelationshipType === 'AffiliateAreaManager'
            || editModel.userRelationshipType === 'AffiliateDistrictManager'
        ) {

            let affiliate = affiliates.find(function (a) {
                return a.id === editModel.userRelationshipId;
            });

            if (affiliate !== null) {
                selectedAffiliateDropdown = {
                    value: affiliate.id,
                    label: affiliate.name,
                    id: affiliate.id
                };
            }
        }
        let welcomeEmailSent = editModel.welcomeEmailSent;
        this.setState({
            isLoading: false, editModel, originalEmail, hospitals, affiliates, affiliateGroups, gpos, gpoGroups,
            selectedHospitalDropdown, selectedAffiliateDropdown, welcomeEmailSent
        });
    }

    onClickCancel = () => {
        if (!this.state.isDirty)
            this.props.onCancel();
        else
            this.setState({ showCancelModal: true });
    }

    saveUser = async () => {
        let id = await api.saveUserEditModel(this.state.editModel);
        this.props.onSaveSuccess(true);

        this.setState(prevState => ({
            editModel: {
                ...prevState.editModel,
                id,
                isNew: false
            },
            isDirty: false
        }));
    }

    getEmptyEditModel = () => {
        return {
            roleOptions: [],
            isNew: true,
            id: "",
            userName: "",
            fullName: "",
            email: "",
            role: "",
            userRelationshipType: null,
            userRelationshipId: null,
            userRelationshipGroupId: null,
            isActive: true
        };
    }

    handleTextBlur = event => {
        this.setState({
            isDirty: true,
            editModel: {
                ...this.state.editModel,
                [event.target.attributes.id.value]: event.target.value
            }
        }, this.refreshOptions);
    }

    handleHospitalChange = item => {
        this.setState({
            isDirty: true,
            selectedHospitalDropdown: item,
            editModel: {
                ...this.state.editModel,
                userRelationshipId: item.id
            }
        });
    }

    handleAffiliateChange = item => {
        this.setState({
            isDirty: true,
            selectedAffiliateDropdown: item,
            editModel: {
                ...this.state.editModel,
                userRelationshipId: item.id
            }
        });
    }

    handleSelectChange = event => {
        this.setState({
            isDirty: true,
            editModel: {
                ...this.state.editModel,
                [event.target.name]: event.target.value
            }
        }, this.refreshOptions);
    }

    handleChange = event => {
        if (!this.state.isDirty) {
            this.setState({ isDirty: true });
        }
    }

    refreshOptions = () => {
        let userRelationshipId = this.state.userRelationshipId;
        let userRelationshipType = this.state.userRelationshipType;
        let userRelationshipGroupId = this.state.userRelationshipGroupId;

        if (this.state.role !== "User" || userRelationshipType === UserRelationshipTypes.GpoEnterprise || userRelationshipType === UserRelationshipTypes.Affiliate)
            userRelationshipGroupId = null;

        if (this.state.role !== "User") {
            userRelationshipId = null;
            userRelationshipType = null;
        }

        this.setState({ userRelationshipId, userRelationshipType, userRelationshipGroupId });
    }

    showResetPasswordModal = () => {
        this.setState({ showResetPasswordModal: true });
    }

    onResetPasswordCancel = () => {
        this.setState({ showResetPasswordModal: false });
    }

    onResetPasswordOK = async () => {
        await api.sendPasswordResetEmail(this.state.editModel.id);
        this.setState({ showResetPasswordModal: false });
    }

    renderResetPasswordModal = (columnSpread) => {
        return (
            <ModalOKCancel
                title="Reset Password"
                body={`Send Password Reset email to ${this.state.editModel.email}?`}
                show={this.state.showResetPasswordModal}
                onOK={this.onResetPasswordOK.bind(this)}
                onCancel={this.onResetPasswordCancel.bind(this)}
            />
        );
    }

    showWelcomeEmailModal = () => {
        this.setState({ showWelcomeEmailModal: true });
    }

    onWelcomeEmailCancel = () => {
        this.setState({ showWelcomeEmailModal: false });
    }

    onWelcomeEmailOK = async () => {
        let welcomeEmailSent = await api.sendWelcomeEmail(this.state.editModel.id);
        this.setState({ showWelcomeEmailModal: false , welcomeEmailSent });
    }

    renderWelcomeEmailModal = (columnSpread) => {
        return (
            <ModalOKCancel
                title="Send Welcome Email"
                body={`Send Welcome email to ${this.state.editModel.email}?`}
                show={this.state.showWelcomeEmailModal}
                onOK={this.onWelcomeEmailOK.bind(this)}
                onCancel={this.onWelcomeEmailCancel.bind(this)}
            />
        );
    }

    renderCancelModal = (columnSpread) => {
        return (
            <ModalOKCancel
                title="Discard Changes"
                body={"You have unsaved changes. Click OK to discard your changes."}
                show={this.state.showCancelModal}
                onOK={() => { this.props.onCancel(); this.setState({showCancelModal: false}); }}
                onCancel={() => this.setState({ showCancelModal: false })}
            />
        );
    }


    renderButtons = (columnSpread) => {
        return (
            <Grid item xs={columnSpread} style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }} >
                {!this.state.editModel.isNew && !this.state.welcomeEmailSent &&
                    <Button variant='contained' color='default' style={{ marginRight: '15px' }} onClick={this.showWelcomeEmailModal} disabled={this.state.isDirty}>
                        Send Welcome Email
                    </Button>
                }
                {this.state.welcomeEmailSent &&
                    <p style={{ marginRight: '15px' }}>
                    Welcome email was sent at {this.state.welcomeEmailSent.substr(this.state.welcomeEmailSent.indexOf(' ') + 1)}{' '} (PST) {' '}
                    on {this.state.welcomeEmailSent.substr(0, this.state.welcomeEmailSent.indexOf(' '))}
                    </p>
                }
                {!this.state.editModel.isNew &&
                    <Button variant='contained' color='default' style={{ marginRight: '15px' }} onClick={this.showResetPasswordModal} disabled={this.state.isDirty}>
                        Send Password Reset
                    </Button>
                }
                <Button variant='contained' color='default' style={{ marginRight: '15px', width: '80px' }} onClick={this.onClickCancel.bind(this)}>
                    Cancel
                </Button>
                <Button variant='contained' color='primary' style={{ width: '80px' }} onClick={this.saveUser} disabled={!this.state.isDirty}>
                    Save
                </Button>
            </Grid>
        );
    }

    handleCheckboxChange = event => {
        this.setState({ editModel: { ...this.state.editModel, [event.target.name]: event.target.checked }, isDirty: true });
    }

    render() {
        let isNew = this.props.userId === null;

        return (
            <div>
                { this.state.isLoading &&
                    <span>Loading...</span>
                }
                { !this.state.isLoading &&
                    <Grid container spacing={24}>
                        <Grid item xs={4}>
                            <EditorHeader text={isNew ? 'Add User' : 'Edit User'} isNew={isNew} />
                        </Grid>
                        {this.renderButtons(8)}

                        {/*Full Name*/}
                        <Grid item xs={4}>
                            <TextField id='fullName' label='Full Name'
                                defaultValue={this.state.editModel.fullName} key={this.state.editModel.fullName}
                                onBlur={this.handleTextBlur} onChange={this.handleChange}
                                fullWidth inputProps={{ maxLength: "50" }}
                            />
                        </Grid>
                        <Grid item xs={8} />

                        {/*email*/}
                        <Grid item xs={4}>
                            <TextField id='email' label='Email'
                                defaultValue={this.state.editModel.email} key={this.state.editModel.email}
                                onBlur={this.handleTextBlur} onChange={this.handleChange}
                                fullWidth inputProps={{ maxLength: "50" }}
                            />
                        </Grid>

                        {/*role*/}
                        <Grid item xs={4}>
                            <FormControl fullWidth>
                                <InputLabel htmlFor='role'>Role</InputLabel>
                                <Select
                                    value={this.state.editModel.role || ''}
                                    onChange={this.handleSelectChange}
                                    inputProps={{ name: 'role', id: 'role', key: 'role' }}
                                >
                                    {this.state.editModel.roleOptions.map(x =>
                                        <MenuItem key={x} value={x}>{x}</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={4} />

                        { /*User relationship type & ID.*/ }
                    <Grid item xs={4}>
                        {(this.state.editModel.role === "User" || this.state.editModel.role === UserRoles.AffiliateAnalyticsManager) &&
                                <FormControl fullWidth>
                                    <InputLabel htmlFor='userRelationshipType'>Relationship Type</InputLabel>
                                    <Select
                                        value={this.state.editModel.userRelationshipType || ''}
                                        onChange={this.handleSelectChange}
                                        inputProps={{ name: 'userRelationshipType', id: 'userRelationshipType', key: 'userRelationshipType' }}
                                    >
                                        {this.state.editModel.userRelationshipTypeOptions.map(x =>
                                            <MenuItem key={x} value={x}>{x}</MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            }
                        </Grid>

                        <Grid item xs={4}>
                            { /* Hospital Selector */}
                            {this.state.editModel.role === 'User' && this.state.editModel.userRelationshipType === 'Hospital' &&
                                <div style={{ marginTop: '10px' }}>
                                    <TypeAheadSelect
                                        value={this.state.selectedHospitalDropdown}
                                        onChange={this.handleHospitalChange}
                                        placeholder='Hospital'
                                        options={this.state.hospitals.map(hospital => ({ value: hospital.name, label: hospital.name + ' - ' + hospital.description, id: hospital.id }))}
                                    />
                                </div>
                            }

                            { /* Affiliate Selector */}
                            {(this.state.editModel.role === "User" || this.state.editModel.role === UserRoles.AffiliateAnalyticsManager)
                                && (this.state.editModel.userRelationshipType === 'Affiliate'
                                    || this.state.editModel.userRelationshipType === 'AffiliateAreaManager'
                                    || this.state.editModel.userRelationshipType === 'AffiliateDistrictManager') &&
                                //<FormControl fullWidth>
                                //    <InputLabel htmlFor='userRelationshipId'>Affiliate</InputLabel>
                                //    <Select
                                //        value={this.state.editModel.userRelationshipId || ''}
                                //        onChange={this.handleSelectChange}
                                //        inputProps={{ name: 'userRelationshipId', id: 'userRelationshipId', key: 'userRelationshipId' }}
                                //    >
                                //        {this.state.affiliates.map(x =>
                                //            <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>
                                //        )}
                                //    </Select>
                                //</FormControl>
                                <div style={{ marginTop: '10px' }}>
                                    <TypeAheadSelect
                                        value={this.state.selectedAffiliateDropdown}
                                        onChange={this.handleAffiliateChange}
                                        placeholder='Affiliate'
                                        options={this.state.affiliates.map(affiliate => ({ value: affiliate.id, label: affiliate.name, id: affiliate.id }))}
                                    />
                                </div>
                            }

                            { /* GPO Selector */}
                            {this.state.editModel.role === 'User'
                                && (this.state.editModel.userRelationshipType === 'GpoEnterprise'
                                    || this.state.editModel.userRelationshipType === 'GpoAreaManager'
                                    || this.state.editModel.userRelationshipType === 'GpoDistrictManager') &&
                                <FormControl fullWidth>
                                    <InputLabel htmlFor='userRelationshipId'>GPO</InputLabel>
                                    <Select
                                        value={this.state.editModel.userRelationshipId || ''}
                                        onChange={this.handleSelectChange}
                                        inputProps={{ name: 'userRelationshipId', id: 'userRelationshipId', key: 'userRelationshipId' }}
                                    >
                                        {this.state.gpos.map(x =>
                                            <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            }
                        </Grid>

                        <Grid item xs={4}>
                            { /* Affiliate Group Selector */}
                            {(this.state.editModel.role === 'User' || this.state.editModel.role === UserRoles.AffiliateAnalyticsManager)
                                && (this.state.editModel.userRelationshipType === 'AffiliateAreaManager'
                                    || this.state.editModel.userRelationshipType === 'AffiliateDistrictManager') &&
                                <FormControl fullWidth>
                                    <InputLabel htmlFor='userRelationshipGroupId'>Affiliate Group</InputLabel>
                                    <Select
                                        value={this.state.editModel.userRelationshipGroupId || ''}
                                        onChange={this.handleSelectChange}
                                        inputProps={{ name: 'userRelationshipGroupId', id: 'userRelationshipGroupId', key: 'userRelationshipGroupId' }}
                                    >
                                        {this.state.affiliateGroups.filter((value) => value.affiliateId === this.state.editModel.userRelationshipId).map(x =>
                                            <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            }

                            { /* GPO Group Selector */}
                            {this.state.editModel.role === 'User'
                                && (this.state.editModel.userRelationshipType === 'GpoAreaManager'
                                    || this.state.editModel.userRelationshipType === 'GpoDistrictManager') &&
                                <FormControl fullWidth>
                                    <InputLabel htmlFor='userRelationshipGroupId'>GPO Group</InputLabel>
                                    <Select
                                        value={this.state.editModel.userRelationshipGroupId || ''}
                                        onChange={this.handleSelectChange}
                                        inputProps={{ name: 'userRelationshipGroupId', id: 'userRelationshipGroupId', key: 'userRelationshipGroupId' }}
                                    >
                                        {this.state.gpoGroups.filter((value) => value.gpoOptionId === this.state.editModel.userRelationshipId).map(x =>
                                            <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            }
                        </Grid>

                        { /*isActive*/}
                        <Grid item xs={4}>
                            <FormSwitch
                                name="isActive"
                                label="Active"
                                checked={this.state.editModel.isActive}
                                onChange={(e) => this.handleCheckboxChange(e)}
                                color="primary"
                            />
                        </Grid>
                    </Grid>
                }

                {!this.state.editModel.isNew && this.renderResetPasswordModal()}
                {!this.state.editModel.isNew && this.renderWelcomeEmailModal()}
                {this.renderCancelModal()}
            </div>
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/Admin/UserEditor.js